/**
 * LeadClaimCard Component
 * 
 * Individual card displaying a hot lead's semi-blind information.
 * Shows deal score, opportunity type, savings estimate, and claim button.
 * PII (name, phone, email) is hidden until claimed.
 */

import React, { useMemo } from 'react';
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Badge,
  Button,
  Icon,
  Tooltip,
  useColorModeValue,
  Skeleton,
  Tag,
  TagLabel,
  TagLeftIcon,
  Divider,
} from '@chakra-ui/react';
import { 
  FiDollarSign, 
  FiMapPin, 
  FiTrendingUp,
  FiZap,
  FiHome,
  FiPercent,
  FiArrowRight,
  FiTarget,
  FiClock,
  FiStar,
} from 'react-icons/fi';

/**
 * Get color scheme for deal score
 */
function getScoreColor(score) {
  if (score >= 80) return { bg: 'green.500', text: 'white', ring: 'green.300' };
  if (score >= 60) return { bg: 'blue.500', text: 'white', ring: 'blue.300' };
  if (score >= 40) return { bg: 'yellow.500', text: 'gray.800', ring: 'yellow.300' };
  return { bg: 'gray.500', text: 'white', ring: 'gray.300' };
}

/**
 * Format currency value
 */
function formatCurrency(value, compact = false) {
  if (!value && value !== 0) return 'N/A';
  
  const num = typeof value === 'string' ? parseFloat(value.replace(/[,$]/g, '')) : value;
  if (isNaN(num)) return 'N/A';
  
  if (compact && num >= 1000) {
    return `$${(num / 1000).toFixed(0)}K`;
  }
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

/**
 * Get opportunity type label and icon
 */
function getOpportunityInfo(type) {
  const types = {
    'VA_IRRRL': { label: 'VA IRRRL', icon: FiStar, color: 'purple' },
    'RATE_REDUCTION': { label: 'Rate Reduction', icon: FiPercent, color: 'blue' },
    'FHA_STREAMLINE': { label: 'FHA Streamline', icon: FiZap, color: 'teal' },
    'PMI_REMOVAL': { label: 'PMI Removal', icon: FiDollarSign, color: 'green' },
    'CASH_OUT': { label: 'Cash Out', icon: FiDollarSign, color: 'orange' },
    'ARM_CONVERSION': { label: 'ARM Conversion', icon: FiTrendingUp, color: 'red' },
    'TERM_REDUCTION': { label: 'Term Reduction', icon: FiClock, color: 'cyan' },
    'DTI_REDUCTION': { label: 'DTI Reduction', icon: FiTarget, color: 'pink' },
  };
  return types[type] || { label: type || 'Opportunity', icon: FiTarget, color: 'gray' };
}

export function LeadClaimCard({ 
  lead, 
  onClaim, 
  isClaiming = false,
  isBlocked = false,
}) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const highlightBg = useColorModeValue('blue.50', 'blue.900');

  const scoreColors = useMemo(() => getScoreColor(lead.dealScore), [lead.dealScore]);
  const opportunityInfo = useMemo(() => getOpportunityInfo(lead.primaryOpportunity), [lead.primaryOpportunity]);

  const isHighPriority = lead.dealScore >= 80;

  return (
    <Box
      p={4}
      bg={cardBg}
      borderRadius="lg"
      borderWidth="2px"
      borderColor={isHighPriority ? scoreColors.ring : borderColor}
      boxShadow={isHighPriority ? 'md' : 'sm'}
      transition="all 0.2s"
      _hover={{
        boxShadow: 'lg',
        borderColor: scoreColors.ring,
        transform: 'translateY(-2px)',
      }}
      position="relative"
      overflow="hidden"
    >
      {/* High Priority Indicator */}
      {isHighPriority && (
        <Box
          position="absolute"
          top={0}
          right={0}
          bg={scoreColors.bg}
          color={scoreColors.text}
          fontSize="xs"
          fontWeight="bold"
          px={2}
          py={0.5}
          borderBottomLeftRadius="md"
        >
          HOT LEAD
        </Box>
      )}

      <Flex gap={4} align="stretch">
        {/* Deal Score Circle */}
        <Flex
          direction="column"
          align="center"
          justify="center"
          minW="70px"
        >
          <Box
            w="60px"
            h="60px"
            borderRadius="full"
            bg={scoreColors.bg}
            color={scoreColors.text}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            boxShadow={`0 0 0 3px ${scoreColors.ring}`}
          >
            <Text fontSize="xl" fontWeight="bold" lineHeight="1">
              {lead.dealScore || '?'}
            </Text>
            <Text fontSize="8px" textTransform="uppercase" opacity={0.8}>
              score
            </Text>
          </Box>
        </Flex>

        {/* Lead Info */}
        <VStack flex="1" align="stretch" spacing={2}>
          {/* Top Row: Opportunity Type & Tags */}
          <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
            <Tag size="md" colorScheme={opportunityInfo.color} variant="subtle">
              <TagLeftIcon as={opportunityInfo.icon} />
              <TagLabel fontWeight="medium">{opportunityInfo.label}</TagLabel>
            </Tag>
            
            <HStack spacing={2}>
              {lead.mortgageType && (
                <Badge colorScheme="gray" variant="outline" fontSize="xs">
                  {lead.mortgageType}
                </Badge>
              )}
              {lead.borrowerState && (
                <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                  <Icon as={FiMapPin} mr={1} />
                  {lead.borrowerState}
                </Badge>
              )}
            </HStack>
          </Flex>

          {/* Savings Highlight */}
          {lead.estimatedMonthlySavings && (
            <Box 
              bg={highlightBg} 
              px={3} 
              py={2} 
              borderRadius="md"
              borderLeftWidth="3px"
              borderLeftColor="green.500"
            >
              <Flex align="center" gap={2}>
                <Icon as={FiDollarSign} color="green.500" />
                <Text fontWeight="bold" color="green.600">
                  Save {formatCurrency(lead.estimatedMonthlySavings)}/mo
                </Text>
              </Flex>
            </Box>
          )}

          {/* Stats Row */}
          <HStack spacing={4} fontSize="sm" color={mutedText} wrap="wrap">
            {lead.currentBalance && (
              <Tooltip label="Current Loan Balance">
                <HStack spacing={1}>
                  <Icon as={FiHome} />
                  <Text>{formatCurrency(lead.currentBalance, true)}</Text>
                </HStack>
              </Tooltip>
            )}
            {lead.interestRate && (
              <Tooltip label="Current Interest Rate">
                <HStack spacing={1}>
                  <Icon as={FiPercent} />
                  <Text>{lead.interestRate}%</Text>
                </HStack>
              </Tooltip>
            )}
            {lead.recycleCount > 0 && (
              <Tooltip label="Previously unclaimed">
                <Badge colorScheme="orange" variant="subtle" fontSize="xs">
                  Recycled x{lead.recycleCount}
                </Badge>
              </Tooltip>
            )}
          </HStack>

          {/* "The Why" Summary */}
          {lead.loanSummary && (
            <>
              <Divider />
              <Text fontSize="sm" color={mutedText} noOfLines={2}>
                <Text as="span" fontWeight="medium" color="gray.700">Why: </Text>
                {lead.loanSummary}
              </Text>
            </>
          )}
        </VStack>

        {/* Claim Button */}
        <Flex align="center" pl={2}>
          <Tooltip
            label={isBlocked ? 'Work your existing leads to unlock claiming' : 'Claim this lead'}
            placement="left"
          >
            <Button
              colorScheme={isHighPriority ? 'green' : 'blue'}
              size="lg"
              rightIcon={<FiArrowRight />}
              onClick={() => onClaim(lead.id)}
              isLoading={isClaiming}
              loadingText="Claiming"
              isDisabled={isBlocked}
              px={6}
              _hover={{
                transform: 'scale(1.05)',
              }}
              transition="all 0.2s"
            >
              Claim
            </Button>
          </Tooltip>
        </Flex>
      </Flex>

      {/* Existing Contact Warning */}
      {lead.existingContactId && (
        <Box mt={3} pt={3} borderTopWidth="1px" borderTopColor={borderColor}>
          <Badge colorScheme="yellow" variant="subtle">
            <Icon as={FiClock} mr={1} />
            Existing contact in system
          </Badge>
        </Box>
      )}
    </Box>
  );
}

export default LeadClaimCard;
